import MarkdownLite from "@/components/MarkdownLite";
import { ShieldCheck, BookMarked, MessageSquareText, AlertTriangle, Landmark, ExternalLink } from "lucide-react";

const LABELS = [
  { key: "Applicable authority", icon: Landmark },
  { key: "Rule", icon: BookMarked },
  { key: "Plain-English explanation", icon: MessageSquareText },
  { key: "Potential controlling authority", icon: AlertTriangle },
  { key: "Confidence", icon: ShieldCheck },
  { key: "Official source", icon: ExternalLink },
];

function parseSections(text) {
  const out = {};
  const positions = [];
  LABELS.forEach((l) => {
    const re = new RegExp(`\\*\\*${l.key.replace(/[-/]/g, "\\$&")}:\\*\\*`, "i");
    const m = text.match(re);
    if (m) positions.push({ key: l.key, start: m.index, len: m[0].length });
  });
  if (positions.length < 3) return null;
  positions.sort((a, b) => a.start - b.start);
  positions.forEach((p, i) => {
    const end = i + 1 < positions.length ? positions[i + 1].start : text.length;
    out[p.key] = text.slice(p.start + p.len, end).trim();
  });
  return out;
}

function confidenceColor(v = "") {
  const s = v.toLowerCase();
  if (s.includes("high")) return "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10";
  if (s.includes("medium")) return "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10";
  if (s.includes("low")) return "text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10";
  return "text-muted-foreground border-border";
}

export default function StructuredAnswer({ content, sources = [] }) {
  const sections = parseSections(content || "");
  if (!sections) {
    return (
      <div>
        <MarkdownLite text={content} />
        {sources.length > 0 && <SourceRow sources={sources} />}
      </div>
    );
  }
  return (
    <div className="space-y-px border border-border/60 bg-background/40">
      {LABELS.map((l) => {
        const val = sections[l.key];
        if (!val) return null;
        const Icon = l.icon;
        const isConf = l.key === "Confidence";
        return (
          <div key={l.key} className="p-3.5 bg-card/60 border-l-2 border-primary/40">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={1.75} />
              <span className="overline text-primary">{l.key}</span>
            </div>
            {isConf ? (
              <span className={`inline-block text-xs font-mono-tech font-semibold px-2.5 py-1 border ${confidenceColor(val)}`}>
                {val.replace(/[*_.]/g, "").trim() || "—"}
              </span>
            ) : (
              <MarkdownLite text={val} className="text-foreground/90" />
            )}
          </div>
        );
      })}
      {sources.length > 0 && <SourceRow sources={sources} />}
    </div>
  );
}

function SourceRow({ sources }) {
  return (
    <div className="p-3.5 bg-card/40">
      <div className="overline text-muted-foreground mb-2">Retrieved authority</div>
      <div className="flex flex-wrap gap-2">
        {sources.map((s, i) => (
          <a key={i} href={s.source_url} target="_blank" rel="noreferrer" data-testid={`source-link-${i}`}
             className="inline-flex items-center gap-1.5 text-xs font-mono-tech px-2 py-1 border border-border hover:border-primary/50 hover:text-primary transition-colors">
            <ExternalLink className="w-3 h-3" /> {s.citation}
          </a>
        ))}
      </div>
    </div>
  );
}
